import React, { useState, useEffect } from 'react';
import { getCsrfToken } from '../utils/auth';
import '../styles/ReportPicker.css';

/**
 * The instant-report gallery — the whole creation flow for a report panel.
 *
 * There is no wizard behind any of these: a report owns no filters, so picking one IS
 * creating it. That is the same shape as the People panel's menu item, one step further:
 * a menu of one became a menu of many, so it needs a surface of its own rather than a
 * dozen more items on the Add menu.
 *
 * The catalog is fetched, never hard-coded here. A thirteenth report is a builder on the
 * backend plus a catalog row; this component is meant to need no change for it.
 *
 * The catalog is filtered BY MODULE on the server (`reports_engine.catalog_for`), so this
 * gallery only ever offers reports that will appear on the dashboard behind it.
 */
/* The order the groups are shown in. Fixed here rather than derived from the catalog so the
   gallery reads the same way every time, whichever dashboard opened it. */
const GROUPS = ['Training & quizzes', 'Traceability', 'Platform'];

function ReportPicker({ module = 'trace', onClose, onCreated }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await fetch(`/api/reports/catalog?module=${encodeURIComponent(module)}`,
          { credentials: 'include' });
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.detail || 'Could not load the report list');
        if (live) setReports(body.reports || []);
      } catch (err) {
        if (live) setError(err.message);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [module]);

  // Escape closes, like every other modal in the app.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const create = async (report) => {
    setCreating(report.key);
    setError('');
    try {
      const r = await fetch('/api/reports', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({
          report_key: report.key,
          module,
          name: report.title,
          is_public: true,
          owner_only_edit: true,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `Could not create the panel (${r.status})`);
      // Where it LANDED, not where it was asked for: a Traceability report picked from the
      // Training gallery is listed on the Traceability dashboard, and sending the user to the
      // other one would look like the panel was never created.
      onCreated(body.id, body.module || module);
    } catch (err) {
      setError(err.message);
      setCreating('');
    }
  };

  return (
    <div className="rpk-backdrop" onClick={onClose}>
      <div className="rpk-modal" role="dialog" aria-modal="true" aria-label="Add an instant report"
           onClick={(e) => e.stopPropagation()}>
        <div className="rpk-bar">
          <div>
            <h3>Instant reports</h3>
            <p>Nothing to set up. Each one reads snapshots this account has already built.</p>
          </div>
          <button className="rpk-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        {error && <div className="rpk-error">{error}</div>}

        <div className="rpk-body">
          {loading ? (
            <div className="rpk-loading">Loading…</div>
          ) : (
            /* Only what THIS dashboard will list — the catalog endpoint is already filtered
               by module, so every tile here creates a card on the screen behind the modal.
               The groups still show because the platform-wide reports (freshness, cost,
               digest) belong to neither half and are worth naming as such. */
            GROUPS.map((group) => {
              const items = reports.filter((r) => r.group === group);
              if (!items.length) return null;
              return (
                <section className="rpk-group" key={group}>
                  <h4 className="rpk-group-label">{group}</h4>
                  <div className="rpk-tiles">
                    {items.map((r) => (
                      <button
                        key={r.key}
                        className="rpk-tile"
                        onClick={() => create(r)}
                        disabled={!!creating}
                      >
                        <span className="rpk-tt">
                          <span className="rpk-glyph">{String(r.n).padStart(2, '0')}</span>
                          {r.title}
                        </span>
                        <span className="rpk-td">{r.blurb}</span>
                        <span className={`rpk-chip${creating === r.key ? ' rpk-chip-busy' : ''}`}>
                          {creating === r.key ? 'Creating…' : 'Ready now'}
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>

        <div className="rpk-foot">
          Instant reports read only data this account already holds. Nothing is fetched from
          Orcanos when you add one, and none of them can trigger a build.
        </div>
      </div>
    </div>
  );
}

export default ReportPicker;
